import clsx from "clsx";
import { useState } from "react";
import { initialsAvatarDataUrl } from "@compass/shared/utils/initialsAvatar";

type AvatarType = {
    photoUrl?: string | null;
    className?: string;
    /** Display name — used to derive initials when `initials` is not provided. */
    name?: string | null;
    /**
     * Server-computed initials (1–2 chars). Preferred over `name` when present
     * because it's consistent with what the server returns on the author DTO.
     */
    initials?: string;
    /** Stable seed used to pick a color from the palette. Defaults to initials. */
    seed?: string | number;
};

const deriveInitials = (name: AvatarType["name"]): string => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    const first = parts[0]!.charAt(0).toUpperCase();
    const last = parts.length > 1 ? parts[parts.length - 1]!.charAt(0).toUpperCase() : "";
    return first + last;
};

export const Avatar = ({ className, photoUrl, name, initials, seed }: AvatarType) => {
    // PRTL-1349: one <img> per avatar. The initials tile paints as its
    // background until the photo arrives, and replaces it if the photo fails —
    // no hidden probe image doubling the requests on avatar-heavy lists.
    const [failedUrl, setFailedUrl] = useState<string | null>(null);

    const resolvedInitials = initials ?? deriveInitials(name);
    const fallbackSrc = initialsAvatarDataUrl({
        initials: resolvedInitials,
        seed: seed ?? resolvedInitials,
    });
    const showPhoto = !!photoUrl && failedUrl !== photoUrl;

    return (
        <div className={clsx("flex items-center justify-center rounded-full", className)}>
            <img
                className="h-full w-full rounded-xl bg-cover object-cover"
                src={showPhoto ? photoUrl : fallbackSrc}
                alt={showPhoto ? "User avatar" : resolvedInitials}
                decoding="async"
                style={showPhoto ? { backgroundImage: `url("${fallbackSrc}")` } : undefined}
                onError={showPhoto ? () => setFailedUrl(photoUrl) : undefined}
            />
        </div>
    );
};
